import { useMemo } from 'react';
import { beatInBar } from '@/core/analysis/grid';
import { nextSegmentAfter } from '@/core/analysis/segments';
import type { AnalysisResult } from '@/core/analysis/types';
import { pianoKeys, TUNINGS, type Instrument } from '@/core/instruments/shapes';
import { chordName, type DisplayOptions } from '@/core/theory/display';
import {
  FretDiagram,
  layoutFor,
  PianoDiagram,
  STRING_SPACING,
  useVoicingPrefs,
} from '@/features/chord-diagrams';
import { useTimeIndex } from '@/lib/useTimeIndex';
import styles from './StageView.module.css';

interface StageViewProps {
  result: AnalysisResult;
  options: DisplayOptions;
  instrument: Instrument;
  leftHanded: boolean;
  visualOffsetMs: number;
  /** Flash the next chord this many beats ahead (F4.7); 0 = off. */
  warnBeats: number;
}

/**
 * Stage view (F4.3): the current chord huge, the next chord with a countdown of beats, readable
 * from across the room. Re-renders once per beat.
 */
export function StageView({
  result,
  options,
  instrument,
  leftHanded,
  visualOffsetMs,
  warnBeats,
}: StageViewProps) {
  const { segments, beatGrid: grid } = result;
  const starts = useMemo(() => segments.map((s) => s.start), [segments]);
  const index = useTimeIndex(starts, visualOffsetMs);
  const beat = useTimeIndex(grid.beats, visualOffsetMs);
  const prefs = useVoicingPrefs();

  const current = index >= 0 ? segments[index] : undefined;
  const nextIndex = nextSegmentAfter(segments, Math.max(beat, current?.startBeat ?? -1));
  const next = nextIndex >= 0 ? segments[nextIndex] : undefined;
  const left = next && beat >= 0 ? next.startBeat - beat : null;
  const warn = next?.chord != null && left !== null && left > 0 && left <= warnBeats;
  const position = beat >= 0 ? beatInBar(grid, beat) : 0;
  const fretted = instrument === 'piano' ? null : instrument;
  const chord = current?.chord ?? null;

  return (
    <section aria-label="Stage view" className={styles.stage}>
      <div className={styles.now}>
        <span className={styles.eyebrow}>Now</span>
        <span className={styles.nowName} aria-live="polite" data-testid="stage-now">
          {current ? chordName(chord, options) : '–'}
        </span>
        <div className={styles.dots} aria-hidden="true">
          {Array.from({ length: grid.beatsPerBar }, (_, i) => (
            <span key={i} className={styles.dot} data-on={i + 1 === position || undefined} />
          ))}
        </div>
      </div>

      <div className={styles.diagram} aria-hidden="true">
        {chord &&
          (fretted ? (
            <FretDiagram
              layout={layoutFor(chord, fretted, options, leftHanded, prefs)}
              strings={TUNINGS[fretted].length}
              spacing={STRING_SPACING[fretted]}
            />
          ) : (
            <PianoDiagram keys={pianoKeys(chord, options.transpose)} />
          ))}
      </div>

      <div className={styles.next} data-warn={warn || undefined}>
        <span className={styles.eyebrow}>Next</span>
        <span className={styles.nextName}>{next ? chordName(next.chord, options) : '—'}</span>
        <span className={styles.countdown} data-testid="stage-countdown">
          {left !== null && left > 0 ? `in ${left} ${left === 1 ? 'beat' : 'beats'}` : ''}
        </span>
      </div>
    </section>
  );
}
